import { useCallback, useEffect, useState } from 'react'

/**
 * Pricing review — the human half of the reprice guard.
 *
 * The reprice pipeline NEVER auto-writes marks that look like a wrong
 * share class / unit mismatch (sustained prints beyond the override cap,
 * e.g. the LU1213836080 EUR-class 43x incident). Those land in
 * `flagged_for_review` on reprice:last_run and just sat in logs — this
 * card makes them actionable from the frontend:
 *   • Accept market price — the feed was right, stored value was wrong
 *   • Fix ISIN…           — point the pricer at the correct share class
 *   • Set price…          — manual unit-price override
 *
 * Hidden entirely when there is nothing flagged AND no recent sustained
 * overrides. Every field from the API is treated as nullable.
 */

interface FlaggedMark {
  symbol_or_isin: string | null
  name: string | null
  stored_value: number | null
  offered_price: number | null
  implied_value: number | null
  ratio: number | null
  runs: number | null
  holding_id: number | null
  isin: string | null
  currency: string | null
}

interface SustainedOverride {
  symbol?: string | null
  old_value?: number | null
  new_value?: number | null
  runs?: number | null
}

interface CcyMismatch {
  symbol_or_isin: string | null
  name: string | null
  stored_value: number | null
  offered_price: number | null
  feed_ccy: string | null
  holding_ccy: string | null
  holding_id: number | null
  isin: string | null
  currency: string | null
}

interface PricingReview {
  flagged: FlaggedMark[]
  ccy_mismatches: CcyMismatch[]
  rejected_ccy_mismatch: number
  sustained_overrides: SustainedOverride[]
  ran_at: string | null
}

type Editor = { key: string; mode: 'isin' | 'price'; value: string }

const _authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}`,
})

function _money(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function _rowKey(f: FlaggedMark, i: number): string {
  return `${f.holding_id ?? 'x'}-${f.symbol_or_isin ?? 'x'}-${i}`
}

export function PricingReviewCard() {
  const [data, setData] = useState<PricingReview | null>(null)
  const [editor, setEditor] = useState<Editor | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resolvedMsg, setResolvedMsg] = useState<string | null>(null)

  const fetchReview = useCallback(async () => {
    try {
      const res = await fetch('/api/accounts/pricing-review', { headers: _authHeaders() })
      if (!res.ok) return
      const j = await res.json()
      setData({
        flagged: Array.isArray(j?.flagged) ? j.flagged : [],
        ccy_mismatches: Array.isArray(j?.ccy_mismatches) ? j.ccy_mismatches : [],
        rejected_ccy_mismatch: j?.rejected_ccy_mismatch ?? 0,
        sustained_overrides: Array.isArray(j?.sustained_overrides) ? j.sustained_overrides : [],
        ran_at: j?.ran_at ?? null,
      })
    } catch { /* silent — card simply stays hidden */ }
  }, [])

  useEffect(() => {
    fetchReview()
    const t = setInterval(fetchReview, 60_000)
    return () => clearInterval(t)
  }, [fetchReview])

  const resolve = async (
    f: FlaggedMark,
    key: string,
    action: 'accept_market' | 'set_isin' | 'set_price',
    value?: string,
  ) => {
    setBusyKey(key)
    setError(null)
    try {
      const res = await fetch('/api/accounts/pricing-review/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ..._authHeaders() },
        body: JSON.stringify({
          holding_id: f.holding_id ?? undefined,
          symbol_or_isin: f.symbol_or_isin ?? undefined,
          action,
          value: action === 'set_price' ? Number(value) : value,
        }),
      })
      const j = await res.json().catch(() => null)
      if (!res.ok) {
        setError(j?.detail ?? `Resolve failed (${res.status})`)
        return
      }
      // Optimistic: drop the row now, then re-fetch for the true state.
      setData((prev) => prev ? {
        ...prev,
        flagged: prev.flagged.filter((row, i) => _rowKey(row, i) !== key),
      } : prev)
      setEditor(null)
      setResolvedMsg(
        action === 'set_isin'
          ? `ISIN updated for ${f.name ?? f.symbol_or_isin ?? 'holding'} — repriced next tick`
          : `Price updated for ${f.name ?? f.symbol_or_isin ?? 'holding'}`,
      )
      setTimeout(() => setResolvedMsg(null), 8000)
      fetchReview()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resolve failed')
    } finally {
      setBusyKey(null)
    }
  }

  if (!data) return null
  const flagged = data.flagged ?? []
  const mismatches = data.ccy_mismatches ?? []
  const sustained = data.sustained_overrides ?? []
  if (flagged.length === 0 && mismatches.length === 0 && sustained.length === 0) return null

  return (
    <div className="card p-3 border-amber/40 bg-amber/5 text-xs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-medium text-text-primary">
          Pricing review
          {flagged.length > 0 && (
            <span className="text-amber-light"> · {flagged.length} mark{flagged.length === 1 ? '' : 's'} need{flagged.length === 1 ? 's' : ''} a decision</span>
          )}
        </div>
        {(data.rejected_ccy_mismatch ?? 0) > 0 && (
          <span className="text-rose-light font-mono">
            {data.rejected_ccy_mismatch} currency-mismatch quote{data.rejected_ccy_mismatch === 1 ? '' : 's'} rejected
          </span>
        )}
      </div>
      <p className="mt-1 text-text-muted">
        These marks were held back by the reprice guard — likely a wrong share class or a
        pence/pounds unit mismatch. Nothing is written until you decide.
      </p>

      {error && (
        <div className="mt-2 text-rose-light" role="alert">{error}</div>
      )}
      {resolvedMsg && (
        <div className="mt-2 text-emerald-light" role="status">{resolvedMsg}</div>
      )}

      {flagged.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead>
              <tr className="text-text-muted border-b border-border/60">
                <th className="py-1.5 pr-3 font-medium">Holding</th>
                <th className="py-1.5 pr-3 font-medium">Stored value</th>
                <th className="py-1.5 pr-3 font-medium">Offered price</th>
                <th className="py-1.5 pr-3 font-medium">Implied value</th>
                <th className="py-1.5 pr-3 font-medium">Ratio</th>
                <th className="py-1.5 pr-3 font-medium">Runs</th>
                <th className="py-1.5 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map((f, i) => {
                const key = _rowKey(f, i)
                const busy = busyKey === key
                const editing = editor?.key === key ? editor : null
                return (
                  <tr key={key} className="border-b border-border/40 align-top">
                    <td className="py-1.5 pr-3">
                      <span className="text-text-secondary" title={f.symbol_or_isin ?? undefined}>
                        {f.name ?? f.symbol_or_isin ?? '—'}
                      </span>
                      <span className="block font-mono text-[10px] text-text-muted">
                        {f.symbol_or_isin ?? '—'}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-text-primary">{_money(f.stored_value)}</td>
                    <td className="py-1.5 pr-3 font-mono text-amber-light">{_money(f.offered_price)}</td>
                    <td className="py-1.5 pr-3 font-mono text-amber-light">{_money(f.implied_value)}</td>
                    <td className="py-1.5 pr-3 font-mono text-rose-light">{f.ratio != null ? `${f.ratio}×` : '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-text-muted">{f.runs ?? '—'}</td>
                    <td className="py-1.5">
                      {editing ? (
                        <form
                          className="flex items-center gap-1.5 flex-wrap"
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (!editing.value.trim()) return
                            resolve(f, key, editing.mode === 'isin' ? 'set_isin' : 'set_price', editing.value.trim())
                          }}
                        >
                          <input
                            autoFocus
                            className="input py-0.5 px-1.5 text-xs w-36 font-mono"
                            placeholder={editing.mode === 'isin' ? 'e.g. LU1213836080' : 'Unit price'}
                            inputMode={editing.mode === 'price' ? 'decimal' : undefined}
                            value={editing.value}
                            onChange={(e) => setEditor({ ...editing, value: e.target.value })}
                          />
                          <button type="submit" disabled={busy || !editing.value.trim()} className="btn-primary text-xs px-2 py-1">
                            {busy ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={() => setEditor(null)} className="btn-ghost text-xs px-2 py-1">
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            disabled={busy || f.offered_price == null}
                            onClick={() => resolve(f, key, 'accept_market')}
                            className="btn-primary text-xs px-2 py-1"
                            title="The feed is right — write the offered price (value = qty × price)"
                          >
                            {busy ? 'Saving…' : 'Accept market price'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setEditor({ key, mode: 'isin', value: f.isin ?? '' })}
                            className="btn-secondary text-xs px-2 py-1"
                            title="Wrong share class — set the correct ISIN so the pricer fetches the right one next tick"
                          >
                            Fix ISIN…
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setEditor({ key, mode: 'price', value: '' })}
                            className="btn-secondary text-xs px-2 py-1"
                            title="Manual override — set the unit price yourself"
                          >
                            Set price…
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {mismatches.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60">
          <div className="text-text-secondary font-medium">
            Frozen — the price feed quotes a different currency
          </div>
          <p className="mt-0.5 text-text-muted">
            Usually the stored fund code (ISIN) points at the wrong share class. The stored
            value is kept untouched until you fix the code or set the price yourself.
          </p>
          <div className="mt-1.5 overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead>
                <tr className="text-text-muted border-b border-border/60">
                  <th className="py-1.5 pr-3 font-medium">Holding</th>
                  <th className="py-1.5 pr-3 font-medium">Stored value</th>
                  <th className="py-1.5 pr-3 font-medium">Feed offers</th>
                  <th className="py-1.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((m, i) => {
                  const f: FlaggedMark = {
                    symbol_or_isin: m.symbol_or_isin ?? null,
                    name: m.name ?? null,
                    stored_value: m.stored_value ?? null,
                    offered_price: null, // wrong currency — accept_market must stay off
                    implied_value: null,
                    ratio: null,
                    runs: null,
                    holding_id: m.holding_id ?? null,
                    isin: m.isin ?? null,
                    currency: m.currency ?? null,
                  }
                  const key = `ccy-${_rowKey(f, i)}`
                  const busy = busyKey === key
                  const editing = editor?.key === key ? editor : null
                  return (
                    <tr key={key} className="border-b border-border/40 align-top">
                      <td className="py-1.5 pr-3">
                        <span className="text-text-secondary" title={m.symbol_or_isin ?? undefined}>
                          {m.name ?? m.symbol_or_isin ?? '—'}
                        </span>
                        <span className="block font-mono text-[10px] text-text-muted">
                          {m.symbol_or_isin ?? '—'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-text-primary">
                        {_money(m.stored_value)} {m.holding_ccy ?? ''}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-rose-light">
                        {_money(m.offered_price)} {m.feed_ccy ?? '?'}
                      </td>
                      <td className="py-1.5">
                        {editing ? (
                          <form
                            className="flex items-center gap-1.5 flex-wrap"
                            onSubmit={(e) => {
                              e.preventDefault()
                              if (!editing.value.trim()) return
                              resolve(f, key, editing.mode === 'isin' ? 'set_isin' : 'set_price', editing.value.trim())
                            }}
                          >
                            <input
                              autoFocus
                              className="input py-0.5 px-1.5 text-xs w-36 font-mono"
                              placeholder={editing.mode === 'isin' ? 'Correct ISIN' : 'Unit price'}
                              inputMode={editing.mode === 'price' ? 'decimal' : undefined}
                              value={editing.value}
                              onChange={(e) => setEditor({ ...editing, value: e.target.value })}
                            />
                            <button type="submit" disabled={busy || !editing.value.trim()} className="btn-primary text-xs px-2 py-1">
                              {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button type="button" onClick={() => setEditor(null)} className="btn-ghost text-xs px-2 py-1">
                              Cancel
                            </button>
                          </form>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setEditor({ key, mode: 'isin', value: m.isin ?? '' })}
                              className="btn-primary text-xs px-2 py-1"
                              title="Point the pricer at the correct share class — it reprices next tick"
                            >
                              Fix ISIN…
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setEditor({ key, mode: 'price', value: '' })}
                              className="btn-secondary text-xs px-2 py-1"
                              title="Manual override — set the unit price yourself"
                            >
                              Set price…
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sustained.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60">
          <div className="text-text-secondary font-medium">
            Auto-accepted after sustained prints (worth a glance)
          </div>
          <div className="mt-1 flex flex-col gap-0.5">
            {sustained.slice(0, 6).map((s, i) => (
              <div key={`${s.symbol ?? 'x'}-${i}`} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-text-muted">
                <span className="font-mono text-text-secondary">{s.symbol ?? '—'}</span>
                <span className="font-mono">
                  {_money(s.old_value)} → {_money(s.new_value)}
                </span>
                <span className="italic">after {s.runs ?? '?'} consistent runs</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
